import { useEffect, useState } from "react";
import { MarketingLayout } from "../components/MarketingLayout";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "../lib/notices";
import { Check, Zap, Crown, Building2, Sparkles } from "lucide-react";
import { track } from "../lib/analytics";

const PLAN_ORDER = ["free", "creator", "pro", "agency"];
const ICONS = { free: Zap, creator: Sparkles, pro: Crown, agency: Building2, starter: Sparkles };

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState(null);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState("");

  useEffect(() => {
    track("pricing_viewed");
    api.get("/billing/plans").then((r) => setPlans(r.data));
    if (user) api.get("/billing/me").then((r) => setCurrent(r.data));
  }, [user]);

  const buy = async (plan) => {
    if (!user) { nav("/login"); return; }
    setLoading(plan);
    track("checkout_started", { plan });
    try {
      const r = await api.post("/billing/checkout", { plan, origin_url: window.location.origin });
      if (r.data._mock) {
        toast.success("Demo mode: plan upgraded instantly");
        api.get("/billing/me").then((res) => setCurrent(res.data));
        setLoading("");
        return;
      }
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setLoading("");
    }
  };

  const displayPlans = plans ? PLAN_ORDER.filter((k) => plans[k]).map((k) => [k, plans[k]]) : [];
  // A paid plan with no expiry, set by an admin. Any checkout would replace it
  // with one 31-day period, so the server refuses and we don't offer one.
  const comped = !!current?.comped;

  return (
    <MarketingLayout>
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="marquee-tag mb-2">// PRICING</div>
        <h1 className="font-display font-black type-title-lg mb-4 tracking-tighter">SHIP DEMOS<br/><span className="text-brand">AT YOUR PACE.</span></h1>
        <p className="type-lead text-ink-muted max-w-2xl mb-4">Start free with 3 demo projects. Upgrade for launch kits, HD export, and team features.</p>
        {plans?._mock_billing && (
          <p className="text-xs uppercase tracking-widest text-ink-subtle mb-8">Demo billing mode — upgrades apply instantly without Stripe</p>
        )}

        {current && (
          <div className="brutal-card mb-8 flex flex-wrap gap-4 justify-between items-center" data-testid="usage-widget">
            <div>
              <div className="brutal-chip">CURRENT PLAN · {(current.plan || "free").toUpperCase()}</div>
              <div className="mt-2 text-sm">This month: <b>{current.used}</b> / {current.limit} renders</div>
              {comped && (
                <div className="mt-1 text-xs text-ink-muted" data-testid="comped-note">
                  Your plan is complimentary. Contact support to change it.
                </div>
              )}
            </div>
            <div
              className="progress-bar w-64"
              role="progressbar"
              aria-label="Renders used this month"
              aria-valuenow={current.used}
              aria-valuemin={0}
              aria-valuemax={current.limit}
              aria-valuetext={`${current.used} of ${current.limit} renders used`}
            >
              <div className="progress-fill" style={{ width: `${Math.min(100, (current.used / current.limit) * 100)}%` }} />
            </div>
            {current.plan !== "free" && (
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm("Downgrade to Free plan immediately?")) return;
                  await api.post("/billing/cancel");
                  toast.success("Downgraded to Free");
                  api.get("/billing/me").then((r) => setCurrent(r.data));
                }}
                className="brutal-btn brutal-btn-secondary"
                data-testid="btn-cancel-plan"
              >
                Downgrade to Free
              </button>
            )}
          </div>
        )}

        {displayPlans.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 brutal-border" data-testid="pricing-grid">
            {displayPlans.map(([k, p], i) => {
              const Ic = ICONS[k] || Zap;
              const active = current?.plan === k || (k === "creator" && current?.plan === "starter");
              const highlight = k === "pro";
              return (
                <div
                  key={k}
                  className={`p-8 ${i < displayPlans.length - 1 ? "lg:border-r" : ""} brutal-border border-t-0 border-b-0 border-l-0 ${highlight ? "bg-surface-inverse text-white on-ink" : "bg-white"}`}
                  data-testid={`plan-${k}`}
                  aria-current={active ? "true" : undefined}
                >
                  <div className="flex items-center justify-between mb-4">
                    <Ic size={28} />
                    {highlight && <span className="brutal-chip bg-brand text-ink-on-accent border-brand">POPULAR</span>}
                  </div>
                  <h2 className="font-display font-black type-subheading tracking-tighter">{p.name}</h2>
                  <div className="mt-3 mb-6">
                    <span className="font-display font-black text-4xl tracking-tighter">${p.amount}</span>
                    {p.amount > 0 && <span className="text-sm ml-1">/mo</span>}
                    {p.amount > 0 && <div className="text-xs text-ink-subtle mt-1">31-day access · renew anytime</div>}
                  </div>
                  {active && current?.plan_expires_at && (
                    <div className="text-xs text-ink-subtle mb-4" data-testid={`plan-expiry-${k}`}>
                      Active until {new Date(current.plan_expires_at).toLocaleDateString()}
                    </div>
                  )}
                  <ul className="space-y-2 mb-8 text-sm min-h-[140px]">
                    {p.features?.map((f) => (
                      <li key={f} className="flex gap-2 items-start"><Check size={16} className="mt-0.5 flex-shrink-0" />{f}</li>
                    ))}
                  </ul>
                  {k === "free" ? (
                    <button type="button" disabled={active} className="brutal-btn brutal-btn-secondary w-full justify-center" onClick={() => nav(user ? "/dashboard" : "/register")} data-testid={`btn-${k}`}>
                      {active ? "CURRENT" : "Get Started"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={comped || (active && !current?.plan_expires_at) || loading === k}
                      onClick={() => { track("upgrade_clicked", { plan: k }); buy(k); }}
                      className={`brutal-btn w-full justify-center ${highlight ? "" : "brutal-btn-secondary"}`}
                      data-testid={`btn-${k}`}
                    >
                      {active ? (current?.plan_expires_at ? (loading === k ? "…" : "Renew") : "CURRENT") : loading === k ? "…" : `Upgrade`}
                    </button>
                  )}
                  {k !== "free" && !comped && (
                    <div className="text-xs text-ink-subtle mt-2">
                      Switching plans converts your remaining time at the new plan's price.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </MarketingLayout>
  );
}
